import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom'; // Added useNavigate
import { useAuth } from '../context/AuthContext'; // Import useAuth
import { useDataCache } from '../context/DataCacheContext'; // Import useDataCache
import { FaStar, FaMapMarkerAlt, FaClock, FaLightbulb, FaChevronRight, FaChevronLeft, FaTimes, FaRegStar, FaBookmark, FaInfoCircle } from 'react-icons/fa';
import './LocationPopup.css'; // CSS will be moved to this same folder
// No longer directly using these from favoritesService here, they will be called via context
// import { addToFavorites, removeFromFavorites, checkIfFavorite } from '../services/favoritesService'; 

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// REMOVE MOCK FUNCTIONS
// const checkIfFavorite = async (token, cityName, locationId) => { console.log('Mock checkIfFavorite called'); return null; };
// const addToFavorites = async (token, cityName, locationId, locationName) => { console.log('Mock addToFavorites called'); return {success: true }; };
// const removeFromFavorites = async (token, cityName, locationId) => { console.log('Mock removeFromFavorites called'); return { success: true }; };

const LocationPopup = ({ 
    locationId, 
    locationName, 
    locationDescription, 
    locationAddress, 
    locationTips, 
    locationHours,
    locationMoreInfo,
    isImportant,
    cityName,
    onClose,
    onReviewsToggle
}) => {
    const navigate = useNavigate(); // Added this line
    const { currentUser, idToken: token, isLoading: authIsLoading } = useAuth(); // Use the hook
    const { 
        cachedReviews, 
        loadingReviews, 
        loadReviews, 
        refreshReviews, 
        addContextFavorite,      // Added from useDataCache
        removeContextFavorite    // Added from useDataCache
    } = useDataCache(); // Use context
    const [rating, setRating] = useState(3);
    const [comment, setComment] = useState('');
    const [showReviews, setShowReviews] = useState(false);
    const [activeTab, setActiveTab] = useState('info');
    const [isFavorite, setIsFavorite] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const MAX_COMMENT_LENGTH = 200;
    const cacheKey = `${cityName}_${locationId}`;
    
    const currentReviews = useMemo(() => cachedReviews[cacheKey] || [], [cachedReviews, cacheKey]);
    
    const isLoadingCurrentReviews = loadingReviews[cacheKey] || false;

    // Calculate average rating from currentReviews
    const averageRating = React.useMemo(() => {
        if (!currentReviews || currentReviews.length === 0) return 0;
        const totalRating = currentReviews.reduce((sum, review) => sum + review.rating, 0);
        return totalRating / currentReviews.length;
    }, [currentReviews]);

    useEffect(() => {
        if (locationId && cityName) { // Token no longer required to fetch reviews
            loadReviews(cityName, locationId, token);
        }
    }, [locationId, cityName, token, loadReviews]);
    
    useEffect(() => {
        const checkFavoriteStatus = async () => {
            if (currentUser && locationId && cityName && token) { 
                try {
                    // GET /api/favorites/:cityName/:locationId/check
                    const response = await fetch(`${API_BASE_URL}/favorites/${encodeURIComponent(cityName)}/${encodeURIComponent(locationId)}/check`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (!response.ok) {
                        throw new Error(`Failed to check favorite status: ${response.status}`);
                    }
                    const data = await response.json();
                    setIsFavorite(data.isFavorite);
                } catch (error) {
                    console.error("Error checking favorite status:", error);
                    setIsFavorite(false); // Default to false on error
                }
            } else {
                setIsFavorite(false); 
            }
        };
        checkFavoriteStatus();
    }, [currentUser, locationId, cityName, token]);

    const handleSubmitReview = async (e) => {
        e.preventDefault();
        if (!comment.trim()) {
            alert("Please write a comment for your review.");
            return;
        }
        if (!currentUser || !token) {
            alert("You must be logged in to leave a review.");
            return;
        }
        try {
            // POST /api/reviews/:cityName/:locationId/reviews
            // Body: { rating, comment, locationName }
            const response = await fetch(`${API_BASE_URL}/reviews/${encodeURIComponent(cityName)}/${encodeURIComponent(locationId)}/reviews`, { 
               method: 'POST', 
               headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
               body: JSON.stringify({ rating, comment, locationName }) // Added locationName
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to submit review: ${response.status}`);
            }
            // const newReview = await response.json(); // Contains the new review with id
            setComment('');
            setRating(3);
            setShowConfirmation(true);
            setTimeout(() => setShowConfirmation(false), 3000);
            
            // Refresh reviews from cache/API via context
            if (token !== undefined) { // Ensure token is available for refresh
                 await refreshReviews(cityName, locationId, token);
            }

        } catch (error) {
            console.error("Error adding review:", error);
            alert(error.message || "Failed to submit review. Please try again.");
        }
    };

    const toggleReviewsPanel = () => {
        const newState = !showReviews;
        setShowReviews(newState);
        if (onReviewsToggle) {
            onReviewsToggle(newState);
        }
    };

    const handleFavoriteToggle = async (e) => {
        e.stopPropagation();
        if (!currentUser || !token) {
            alert("You must be logged in to add favorites.");
            return;
        }
        try {
            let result;
            if (isFavorite) {
                result = await removeContextFavorite(token, cityName, locationId);
            } else {
                result = await addContextFavorite(token, cityName, locationId, locationName);
            }

            if (result && result.success) {
                setIsFavorite(!isFavorite); // Toggle local state optimistically or based on true success
            } else {
                // Error already logged by context/service, alert a generic message or the specific one if available
                alert(result.message || "Failed to update favorite status. Please try again.");
            }
        } catch (error) { // Should be less likely to hit this if context handles its own errors
            console.error("Error toggling favorite in LocationPopup:", error);
            alert("An unexpected error occurred while updating favorite status.");
        }
    };

    const handleCommentChange = (e) => {
        const value = e.target.value;
        const lineCount = (value.match(/\n/g) || []).length + 1;
        if (value.length <= MAX_COMMENT_LENGTH && lineCount <= 8) {
            const limitedNewlines = value.replace(/\n{3,}/g, '\n\n');
            setComment(limitedNewlines);
        } else if (lineCount > 8 && comment.length < value.length) {
            return;
        } else if (value.length <= MAX_COMMENT_LENGTH) {
            const limitedNewlines = value.replace(/\n{3,}/g, '\n\n');
            setComment(limitedNewlines);
        }
    };

    const reviewFormStarRating = (
        <div className="star-rating">
            {[...Array(5)].map((_, index) => (
                <FaStar 
                    key={index}
                    className={index < rating ? "star filled" : "star"}
                    size={24}
                    onClick={() => setRating(index + 1)}
                    style={{ cursor: 'pointer', marginRight: '4px' }}
                />
            ))}
        </div>
    );
    
    const averageRatingStarsDisplay = (
        <div className="stars-row">
            {[...Array(5)].map((_, index) => (
                <FaStar 
                    key={index}
                    className={index < Math.round(averageRating) ? "star filled" : "star"}
                />
            ))}
            <span className="rating-text">
                {averageRating > 0 
                    ? `${averageRating.toFixed(1)} (${currentReviews.length} reviews)` 
                    : 'No ratings yet'}
            </span>
        </div>
    );

    if (authIsLoading) {
        // Or a more specific loading indicator for the popup content
        return <div className="modern-popup"><p>Loading...</p></div>; 
    }

    return (
        <div className={`modern-popup ${showReviews ? 'reviews-active' : ''}`}>
            <div className={`main-popup ${showReviews ? 'reviews-panel-visible' : ''}`}>
                <div className="popup-main-header">
                    <h2 className="location-main-title">{locationName}</h2>
                    {Boolean(isImportant) && (
                        <span className="important-badge main-important-badge">Important</span>
                        )}
                <button 
                    className={`favorite-bookmark-btn visible ${isFavorite ? 'is-favorite' : ''}`}
                    onClick={handleFavoriteToggle}
                    title={isFavorite ? "Remove from Favorites" : "Add to Favorites"}
                        disabled={!currentUser && !authIsLoading}
                >
                        {isFavorite ? <FaBookmark size={18} /> : <FaRegStar size={18} />}
                </button>
                    <button className="popup-close-btn" onClick={onClose} title="Close"><FaTimes /></button> 
                </div>
               
                <div className="tabs-nav">
                    <button 
                        className={`tab-button ${activeTab === 'info' ? 'active' : ''}`} 
                        onClick={() => setActiveTab('info')}
                    >
                        Info
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'tips' ? 'active' : ''}`} 
                        onClick={() => setActiveTab('tips')}
                    >
                        Tips & More
                    </button>
                    <button 
                        className={`tab-button ${activeTab === 'addReview' ? 'active' : ''}`} 
                        onClick={() => setActiveTab('addReview')}
                        disabled={!currentUser && !authIsLoading}
                        title={!currentUser && !authIsLoading ? "Login to add a review" : "Add your review"}
                    >
                        Add Review
                    </button>
                </div>

                <div className="tab-content-area">
                    {activeTab === 'info' && (
                        <div className="info-tab-content">
                            {isLoadingCurrentReviews && currentReviews.length === 0 && <p>Loading reviews...</p>} 
                            {!isLoadingCurrentReviews && locationDescription && <p className="location-description">{locationDescription}</p>}
                            <div className="location-meta">
                                                            {locationAddress && (
                                <div className="meta-item">
                                    <FaMapMarkerAlt 
                                        className="meta-icon clickable-icon" 
                                        onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationAddress)}`, '_blank')}
                                        title="Open in Google Maps"
                                    />
                                    <span 
                                        className="clickable-address"
                                        onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationAddress)}`, '_blank')}
                                        title="Open in Google Maps"
                                    >
                                        {locationAddress}
                                    </span>
                                </div>
                            )}
                                {locationHours && (
                                    <div className="meta-item">
                                        <FaClock className="meta-icon" />
                                        <span>{locationHours}</span>
                                    </div>
                                )}
                            </div>
                            <div className="rating-summary">
                                {averageRatingStarsDisplay}
                            </div>
                            {isImportant && (
                                 <div 
                                    className="details-link"
                                    onClick={() => navigate(`/location/${cityName}/${locationId}`)}
                                >
                                    View Full Details
                                </div>
                            )}
                            <button className="reviews-toggle-btn" onClick={toggleReviewsPanel}> 
                                {showReviews ? <FaChevronLeft /> : <FaChevronRight />}{' '}
                                {showReviews ? 'Hide Reviews' : `Browse Reviews (${currentReviews.length})`}
                            </button>
                        </div>
                    )}
                    {activeTab === 'tips' && (
                        <div className="tips-tab-content">
                            <h4><FaLightbulb className="meta-icon tips-icon" /> Tips & Advice</h4>
                            {locationTips ? (
                                <p className="location-tips-text">{locationTips}</p>
                            ) : (
                                <p className="no-content-text">No tips available for this location yet.</p>
                            )}

                            {locationMoreInfo && (
                                <div className="additional-info details-section popup-more-info-section">
                                    <h4><FaInfoCircle className="meta-icon info-icon" /> More Information</h4>
                                    {typeof locationMoreInfo === 'string' ? (
                                        <p>{locationMoreInfo}</p>
                                    ) : (
                                        <>
                                            {locationMoreInfo.text && <p>{locationMoreInfo.text}</p>}
                                            {locationMoreInfo.links && locationMoreInfo.links.map((link, index) => (
                                                <a key={index} href={link.url} target="_blank" rel="noopener noreferrer" className="info-link">
                                                    {link.text || 'View Link'}
                                                </a>
                                            ))}
                                        </>
                                    )}
                        </div>
                    )}
                        </div>
                    )}
                    {activeTab === 'addReview' && (
                        <div className="add-review-tab-content">
                            <h4>Add Your Review</h4>
                            {currentUser ? (
                            <form onSubmit={handleSubmitReview} className="compact-review-form">
                                <div className="rating-input">
                                    {reviewFormStarRating}
                                </div>
                                <div className="textarea-container">
                                    <textarea
                                        value={comment}
                                        onChange={handleCommentChange}
                                        placeholder={
                                            !currentUser.is_verified_student
                                                ? "Student verification is required to add a review."
                                                : "Share your experience..."
                                        }
                                        required
                                        className="review-textarea"
                                        maxLength={MAX_COMMENT_LENGTH}
                                        rows="4"
                                        disabled={!currentUser.is_verified_student}
                                    />
                                    <div className="character-count">
                                        <span className="line-limit">
                                                {comment.split('\n').length}/8 lines 
                                        </span>
                                        <span>
                                                {MAX_COMMENT_LENGTH - comment.length} chars left
                                        </span>
                                    </div>
                                </div>
                                {showConfirmation && (
                                    <div className="confirmation-message">
                                        <FaStar className="confirmation-icon" />
                                        Review submitted successfully!
                                    </div>
                                )}
                                <button
                                    type="submit"
                                    className="submit-review-btn"
                                    disabled={!currentUser.is_verified_student}
                                >
                                    Submit Review
                                </button>
                            </form>
                            ) : (
                                <p className="login-prompt-text">Please log in to add a review.</p>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {/* Review Panel is now rendered AFTER main-popup */}
            {showReviews && (
                <div className="reviews-panel">
                    <div className="reviews-header">
                        <h3>Reviews ({currentReviews.length})</h3>
                        <button className="close-reviews-btn" onClick={toggleReviewsPanel} title="Close Reviews"><FaTimes /></button>
                    </div>
                    <div className="reviews-list">
                        {isLoadingCurrentReviews && currentReviews.length === 0 && <p>Loading reviews...</p>}
                        {!isLoadingCurrentReviews && currentReviews.length > 0 ? (
                            currentReviews.map(review => (
                                <div key={review.id || review.comment} className="review-item">
                                    <div className="review-header">
                                        <span className="reviewer">
                                            {review.displayUsername || 'Anonymous User'}
                                        </span>
                                        <div className="review-stars">
                                            {[...Array(5)].map((_, index) => (
                                                <FaStar 
                                                    key={index}
                                                    className={index < review.rating ? "star filled" : "star"}
                                                    size={14}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                    <p className="review-content">{review.comment}</p>
                                    <div className="review-date">
                                        {review.timestamp && typeof review.timestamp.toDate === 'function' 
                                            ? review.timestamp.toDate().toLocaleDateString() 
                                            : (review.timestamp ? new Date(review.timestamp).toLocaleDateString() : 'Unknown date')}
                                    </div>
                                </div>
                            ))
                        ) : (
                            !isLoadingCurrentReviews && <p className="no-reviews">No reviews yet. Be the first to leave a review!</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default LocationPopup; 